import type { ReactNode } from "react"

import { clsx } from "clsx"

import type { ProposalFinancialSummaryDto } from "~/api/dto/proposal-financial-summary.dto"

import {
  charge,
  IncentiveLines,
  reduction,
  TotalIncentivesRow,
} from "./summary-card"
import type { LedgerLine } from "./summary-card"
import { formatUsdExact } from "../../BatteryOnlyProposal/view-helpers"

export const APPLIED_TO_CONTRACT_HEADING = `Applied to your contract`
export const MAY_ALSO_QUALIFY_HEADING = `You may also qualify for`

/**
 * The ledger rows above Net system cost, formatted only: every figure is the
 * server's.
 */
export const financialSummaryLedgerLines = (
  summary: ProposalFinancialSummaryDto,
): LedgerLine[] => {
  const lines: LedgerLine[] = [
    {
      label: `System price`,
      value: formatUsdExact(summary.systemPrice),
    },
    ...summary.discounts.map((discount) => ({
      label: discount.label,
      badge: `DISCOUNT`,
      value: reduction(discount.amount),
    })),
  ]
  if (
    summary.netAmountDue != null &&
    formatUsdExact(summary.netAmountDue) !==
      formatUsdExact(summary.netSystemCost)
  ) {
    lines.push({
      label: `Participate net amount due`,
      emphasis: true,
      value: formatUsdExact(summary.netAmountDue),
    })
  }
  if (summary.participateTax != null) {
    lines.push({
      label: `Participate tax`,
      value: charge(summary.participateTax),
    })
  }
  if (summary.totalIncentives != null) {
    lines.push({
      label: `Incentives`,
      value: reduction(summary.totalIncentives),
    })
  }
  return lines
}

/** Whether the incentives column has anything of the ledger's to show. */
export const hasFinancialSummaryIncentives = (
  summary: ProposalFinancialSummaryDto,
): boolean =>
  summary.incentives.length > 0 ||
  (Array.isArray(summary.additionalIncentives) &&
    summary.additionalIncentives.length > 0)

function IncentiveGroupHeading({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}): JSX.Element {
  return (
    <div
      className={clsx(
        `text-[length:var(--step--2)] font-bold text-bp-gold`,
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * The incentives the contract applies, their total, and the ones the
 * homeowner may also qualify for, which no total counts.
 */
export function FinancialSummaryIncentives({
  summary,
  totalTestId,
}: {
  summary: ProposalFinancialSummaryDto
  totalTestId: string
}): JSX.Element {
  const applied = summary.incentives
  const additional = Array.isArray(summary.additionalIncentives)
    ? summary.additionalIncentives
    : null
  const groupingEnabled = additional != null
  // A total of nothing is not worth a row. Gated on the *displayed* figure, so
  // an amount that rounds away cannot leave "Total Incentives $0.00" standing
  // over an empty list — which is what a VPP-only proposal used to show.
  const showTotal =
    applied.length > 0 &&
    formatUsdExact(summary.totalIncentives ?? 0) !== formatUsdExact(0)

  return (
    <>
      {applied.length > 0 ? (
        groupingEnabled ? (
          <div>
            <IncentiveGroupHeading className="mb-3">
              {APPLIED_TO_CONTRACT_HEADING}
            </IncentiveGroupHeading>
            <IncentiveLines lines={applied} />
          </div>
        ) : (
          <IncentiveLines lines={applied} />
        )
      ) : null}
      {showTotal ? (
        <TotalIncentivesRow
          total={summary.totalIncentives ?? 0}
          testId={totalTestId}
        />
      ) : null}
      {additional != null && additional.length > 0 ? (
        <div className={applied.length > 0 || showTotal ? `mt-6` : undefined}>
          <IncentiveGroupHeading className="mb-3">
            {MAY_ALSO_QUALIFY_HEADING}
          </IncentiveGroupHeading>
          <IncentiveLines lines={additional} />
        </div>
      ) : null}
    </>
  )
}
